import React, {useEffect, useRef} from 'react';
import {KeyboardKeys} from '../../../Enums';

export type TabListProps = {
  children: any;
  orientation: 'vertical' | 'horizontal';
  /** The id of the element with role='tabpanel' associated with this tablist. */
  tabpanelId: string;
} & React.HTMLAttributes<HTMLDivElement>;

const TabList = ({children, orientation, tabpanelId, ...props}: TabListProps) => {
  const tablistRef = useRef<HTMLDivElement>(null);

  const setFocusToContent = () => {
    // Focus on the first focusable element in the tabpanel
    const tabpanel = document.getElementById(tabpanelId);
    if (tabpanel) {
      const firstFocusableElement = tabpanel.querySelector(
        'button, [tabindex]:not([tabindex="-1"]), a[href]:not([href="#"]), input, select, textarea, [tabindex]:not([tabindex="-1"]), [contenteditable]'
      );
      if (firstFocusableElement) {
        (firstFocusableElement as HTMLElement).focus();
      }
    }
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    const tabs = tablistRef.current
      ? Array.from(tablistRef.current.querySelectorAll('[role="tab"]'))
      : [];
    const currentIndex = tabs.indexOf(document.activeElement as HTMLElement);

    if (orientation === 'vertical') {
      if (KeyboardKeys.ARROWUP === event.key) {
        event.preventDefault();
        const prev = Math.max(0, currentIndex - 1);
        (tabs[prev] as HTMLElement)?.focus();
      } else if (KeyboardKeys.ARROWDOWN === event.key) {
        event.preventDefault();
        const next = Math.min(tabs.length - 1, currentIndex + 1);
        (tabs[next] as HTMLElement)?.focus();
      }
    }
    if (orientation === 'horizontal') {
      if (KeyboardKeys.ARROWLEFT === event.key) {
        event.preventDefault();
        const prev = Math.max(0, currentIndex - 1);
        (tabs[prev] as HTMLElement)?.focus();
      } else if (KeyboardKeys.ARROWRIGHT === event.key) {
        event.preventDefault();
        const next = Math.min(tabs.length - 1, currentIndex + 1);
        (tabs[next] as HTMLElement)?.focus();
      }
      if (KeyboardKeys.ARROWDOWN === event.key) {
        event.preventDefault();
        setFocusToContent();
      }
    }
  };

  useEffect(() => {
    const tabElements = tablistRef.current
      ? Array.from(tablistRef.current.querySelectorAll('[role="tab"]'))
      : [];
    tabElements.forEach(tab => {
      tab.addEventListener('keydown', handleKeyDown as EventListener);
    });
    return () => {
      tabElements.forEach(tab => {
        tab.removeEventListener('keydown', handleKeyDown as EventListener);
      });
    };
  }, [children, orientation]);

  return (
    <div role="tablist" ref={tablistRef} {...props}>
      {children}
    </div>
  );
};

export default TabList;
